import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Power, Search, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function EmployeesPage() {
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "", role_ids: [] });
  const [saving, setSaving] = useState(false);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.q = search.trim();
      const [u, r] = await Promise.all([api.get("/users", { params }), api.get("/roles")]);
      setUsers(u.data.items || []);
      setTotal(u.data.total || 0);
      setRoles(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [page, search]);
  useEffect(() => { load(); }, [load]);

  const filtered = users; // server-side filtering now

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", username: "", email: "", password: "", role_ids: [] });
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, username: u.username, email: u.email || "", password: "", role_ids: u.role_ids || [] });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role_ids: form.role_ids };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("تم تحديث الموظف");
      } else {
        await api.post("/users", form);
        toast.success("تم إضافة الموظف");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggle = async (u) => {
    try {
      await api.put(`/users/${u.id}/toggle`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`حذف الموظف "${u.name}"؟`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canCreate = hasPermission("employees.create");
  const canUpdate = hasPermission("employees.update");
  const canDelete = hasPermission("employees.delete");

  return (
    <div data-testid="employees-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">الموظفون</h1>
          <p className="text-muted-foreground mt-1">إدارة حسابات الموظفين وأدوارهم</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} data-testid="add-employee-button" className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 ms-1" /> إضافة موظف
          </Button>
        )}
      </div>

      <div className="gov-card mb-4 p-3 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute top-1/2 -translate-y-1/2 right-3 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="employees-search"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="ابحث بالاسم، اسم المستخدم، أو البريد..."
            className="ps-3 pe-10 h-10"
          />
        </div>
        <div className="text-sm text-muted-foreground font-mono">
          {total}
        </div>
      </div>

      <div className="gov-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start px-4 py-3 font-medium">الاسم</th>
              <th className="text-start px-4 py-3 font-medium">اسم المستخدم</th>
              <th className="text-start px-4 py-3 font-medium">البريد</th>
              <th className="text-start px-4 py-3 font-medium">الأدوار</th>
              <th className="text-start px-4 py-3 font-medium">الحالة</th>
              <th className="text-start px-4 py-3 font-medium w-28">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin inline" />
              </td></tr>
            )}
            {!loading && filtered.map((u) => (
              <tr key={u.id} className="border-b border-border/60 last:border-0 hover:bg-surface/40" data-testid={`employee-row-${u.username}`}>
                <td className="px-4 py-2.5 font-medium">{u.name}</td>
                <td className="px-4 py-2.5 font-mono text-xs">{u.username}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{u.email || "—"}</td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {(u.role_names || []).map((rn) => (
                      <span key={rn} className="px-2 py-0.5 text-xs rounded-full bg-accent text-accent-foreground border border-primary/10">{rn}</span>
                    ))}
                    {!u.role_names?.length && <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </td>
                <td className="px-4 py-2.5">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${u.is_active ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"}`}>
                    {u.is_active ? "نشط" : "موقوف"}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    {canUpdate && (
                      <button data-testid={`edit-employee-${u.username}`} onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-surface-hover text-muted-foreground hover:text-foreground" title="تعديل">
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                    {canUpdate && (
                      <button onClick={() => toggle(u)} className="p-1.5 rounded hover:bg-surface-hover text-muted-foreground hover:text-foreground" title="تفعيل/إيقاف">
                        <Power className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button data-testid={`delete-employee-${u.username}`} onClick={() => remove(u)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="حذف">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && !filtered.length && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">لا يوجد موظفون</td></tr>
            )}
          </tbody>
        </table>
        <Pagination page={page} total={total} limit={limit} onPageChange={setPage} testid="employees-pagination" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "تعديل موظف" : "إضافة موظف جديد"}</DialogTitle>
            <DialogDescription>أدخل بيانات الموظف وحدد الأدوار الممنوحة له</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>الاسم الكامل</Label>
                <Input data-testid="employee-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>اسم المستخدم</Label>
                <Input data-testid="employee-username-input" disabled={!!editing} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>البريد الإلكتروني (اختياري)</Label>
              <Input data-testid="employee-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>كلمة المرور {editing && "(اتركها فارغة لعدم التغيير)"}</Label>
              <Input data-testid="employee-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>الأدوار</Label>
              <div className="grid grid-cols-2 gap-2 p-3 border border-border rounded-sm bg-surface">
                {roles.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      data-testid={`employee-role-${r.name}`}
                      checked={form.role_ids.includes(r.id)}
                      onCheckedChange={(c) => {
                        setForm({
                          ...form,
                          role_ids: c
                            ? [...form.role_ids, r.id]
                            : form.role_ids.filter((x) => x !== r.id),
                        });
                      }}
                    />
                    <span className="text-sm">{r.name} <span className="text-muted-foreground text-xs">— {r.description}</span></span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            <Button onClick={save} disabled={saving} data-testid="save-employee-button" className="bg-primary hover:bg-primary/90">
              {saving && <Loader2 className="w-4 h-4 ms-2 animate-spin" />}
              حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
